import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  isKoreanHoliday,
  getHolidayName,
  isWeekend,
  ServiceOrderCategory,
  type CalendarEventDto,
  type CalendarRenewalDto,
  type CalendarReplacementDto,
} from '@bhops/shared';
import { infrastructureOrdersApi } from '@/api/infrastructure-orders';
import { listAllServiceOrders } from '@/lib/list-all-service-orders';
import { activeCustomersApi } from '@/api/active-customers';
import { scheduleDatesApi } from '@/api/scheduleDates';
import { kstTodayYmd, kstYmdHyphen } from '@/lib/format-date';
import { Icon } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import { CompleteReplacementModal } from './CompleteReplacementModal';
import { CompleteRenewalModal } from './CompleteRenewalModal';
import { renewalSubtitle, renewalTitle } from './renewal-label';
import { EventEditModal } from './EventEditModal';
import { eventChipTitle, eventSubtitle } from './event-label';
import { replacementSubtitle, replacementTitle } from './replacement-label';
import {
  type CalendarCategory,
  CATEGORY_META,
  CATEGORY_ORDER,
  DEFAULT_ACTIVE,
  loadActiveCategories,
  saveActiveCategories,
} from './calendar-categories';
import { maintenanceHref } from '@/lib/maintenance-link';

// Plan v0.3.0 §C-2 (PPTX Slide 3) — "다가오는 일정" right-column panel
// that complements IntegratedCalendar in compact mode. The calendar
// answers "which day are events on?" while this panel answers "what's
// coming up next, in time order?".
//
// FB2 §2 (결정 H) — 패널이 캘린더와 동일한 4-카테고리 모델(설치/발주/납품/
// 유지보수)·아이콘·토글·localStorage 영속을 공유한다 (./calendar-categories).
// 종전에는 자체 3-카테고리(install/delivery/service)에 토글 없이 install +
// delivery 만 방출했다. 이제 캘린더처럼 토글 바를 두고, 켜진 카테고리만
// 방출·표시한다. 저장키는 캘린더와 독립(아래 STORAGE_KEY).
//
// 데이터 소스:
//   🚚 install     ← InfrastructureOrder.installScheduledAt (&& !installCompletedAt)
//   📐 order       ← InfrastructureOrder.grillOrderedAt
//   📦 delivery    ← InfrastructureOrder.grillDeliveredAt
//   🛠️ maintenance ← ServiceOrder.scheduledFor (PREVENTIVE — 유지보수 토글 ON 시 fetch)
//
// Default window: today + next 14 days (configurable via `days` prop).
// Empty state: friendly inline message; we never render an empty card
// silhouette since the dashboard's vertical real estate matters.

// FB2 §2 (결정 H) — 패널 전용 저장키. 캘린더와 분리해 한쪽 토글이 다른 쪽을
// 덮어쓰지 않도록 한다.
const CATEGORY_STORAGE_KEY = 'kuun:upcoming-events:categories';

interface UpcomingEvent {
  id: string;
  date: string; // YYYY-MM-DD
  category: CalendarCategory;
  title: string;
  subtitle?: string;
  href?: string;
  /** 2026-10-19 — 시각 'HH:mm'. 없으면 하루 종일. */
  time?: string | null;
  /** 2026-10-20 — 로봇 교체. 누르면 링크 대신 [교체 완료] 창. */
  replacement?: CalendarReplacementDto;
  /** 2026-10-22 — 재계약. 누르면 [재계약 완료] 창. */
  renewal?: CalendarRenewalDto;
  /** 2026-11-02 — 이벤트(설치 · 회수 칩 각각). 누르면 이벤트 창. */
  calendarEvent?: CalendarEventDto;
}

interface Props {
  /**
   * Lower bound of the visible window (YYYY-MM-DD). Defaults to **KST**
   * today. Pass an explicit value when embedding in tests or when the
   * dashboard wants to anchor the panel to a different reference date.
   *
   * 한국시간 통일 5차(26-08-14) — 종전 기본값은 UTC 오늘이라 한국 오전 0~9시에
   * 창이 **어제부터** 시작했고, 그만큼 뒤쪽 하루(운영자가 기대한 마지막 날)를
   * 놓쳤다("앞으로 14일" 인데 실제로는 어제~13일 뒤).
   */
  from?: string;
  /**
   * Number of days to include starting from `from`. Default 14
   * (two-week look-ahead). Range is inclusive on both ends.
   */
  days?: number;
}

export function UpcomingEventsPanel({ from, days = 14 }: Props) {
  // FB2 §2 (결정 H) — 카테고리 표시 토글 (패널 전용 저장키로 영속).
  const [activeCategories, setActiveCategories] = useState<Record<CalendarCategory, boolean>>(() =>
    loadActiveCategories(CATEGORY_STORAGE_KEY, DEFAULT_ACTIVE),
  );

  useEffect(() => {
    saveActiveCategories(CATEGORY_STORAGE_KEY, activeCategories);
  }, [activeCategories]);

  function toggleCategory(cat: CalendarCategory): void {
    setActiveCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  const range = useMemo(() => {
    const start = from ?? kstTodayYmd();
    // `${ymd}T00:00:00.000Z` 로 만든 **UTC 자정**을 하루씩 민다 — 순수 달력 산술이라
    // 시간대가 없다(자정을 KST 로 읽어도 같은 날 09:00 = 같은 날짜). 라벨은 정본으로.
    const startDate = new Date(`${start}T00:00:00.000Z`);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + days - 1);
    return { start, end: kstYmdHyphen(endDate) ?? '' };
  }, [from, days]);

  const ordersQ = useQuery({
    queryKey: ['dashboard', 'upcoming', range.start, range.end],
    queryFn: () =>
      infrastructureOrdersApi.list({
        installFrom: range.start,
        installTo: range.end,
      }),
    staleTime: 60_000,
    // Same cadence as the dashboard KPI strip — keep the upcoming list
    // gently fresh while users review pipelines.
    refetchInterval: 60_000,
  });

  // FB2 §2 — 유지보수(ServiceOrder) 일정. 유지보수 토글이 켜져 있을 때만
  // fetch (기본 OFF 라 평상시 호출 안 함). 캘린더와 동일하게 예방정비
  // (PREVENTIVE) 의 scheduledFor 를 소스로 사용.
  const maintenanceQ = useQuery({
    queryKey: ['dashboard', 'upcoming', 'maintenance', range.start, range.end],
    enabled: activeCategories.maintenance,
    queryFn: () =>
      // 서버는 한 번에 100건까지 — 200 으로 부르면 400 이라 유지보수가 하나도 안 보였다(2026-10-19).
      listAllServiceOrders({ category: ServiceOrderCategory.PREVENTIVE }),
    staleTime: 60_000,
  });

  // 2026-10-19 — 회수(Customer.returnScheduledAt). 캘린더의 'return' 카테고리 미러.
  const returnsQ = useQuery({
    queryKey: ['dashboard', 'upcoming', 'returns'],
    enabled: activeCategories.return,
    queryFn: () => activeCustomersApi.list(),
    staleTime: 60_000,
  });

  // 2026-10-20 — 로봇 교체('로봇 교체' 서비스 오더). 캘린더의 'replace' 카테고리 미러.
  const replacementsQ = useQuery({
    queryKey: ['dashboard', 'upcoming', 'replacements'],
    enabled: activeCategories.replace,
    queryFn: () => scheduleDatesApi.listReplacements(),
    staleTime: 60_000,
  });

  // 2026-10-22 — 재계약. 캘린더의 'renewal' 카테고리 미러(마친 재계약은 빼고).
  const renewalsQ = useQuery({
    queryKey: ['dashboard', 'upcoming', 'renewals'],
    enabled: activeCategories.renewal,
    queryFn: () => scheduleDatesApi.listRenewals(),
    staleTime: 60_000,
  });

  // 2026-11-02 — 이벤트. 캘린더의 'event' 카테고리 미러(회수를 마친 이벤트는 빼고).
  const eventsQ = useQuery({
    queryKey: ['dashboard', 'upcoming', 'events'],
    enabled: activeCategories.event,
    queryFn: () => scheduleDatesApi.listEvents(),
    staleTime: 60_000,
  });

  // 한국시간 통일 5차(26-08-14) — 날짜 키를 **KST** 로(종전 toYmd = UTC 자르기).
  // IntegratedCalendar 와 같은 값을 같은 기준으로 읽어야 캘린더와 이 패널이
  // 같은 일정을 같은 날로 말한다(저장 관례 세 가지가 섞인 이유는 그쪽 주석 참고).
  const events = useMemo<UpcomingEvent[]>(() => {
    const out: UpcomingEvent[] = [];
    for (const o of ordersQ.data ?? []) {
      if (
        o.installScheduledAt &&
        !o.installCompletedAt &&
        inRange(o.installScheduledAt, range.start, range.end)
      ) {
        out.push({
          id: `${o.id}-install`,
          date: kstYmdHyphen(o.installScheduledAt) ?? '',
          category: 'install',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          time: o.installScheduledTime,
          // FB §3 — 계약 상세 미사용 → 설치 일정 클릭은 고객사 상세로.
          // (InfraOrder DTO 에 customerId 항상 포함.)
          href: `/customers/${o.customerId}`,
        });
      }
      // round6 §1-4(b) — 설치 완료(installCompletedAt). 캘린더 'installDone' 미러.
      if (o.installCompletedAt && inRange(o.installCompletedAt, range.start, range.end)) {
        out.push({
          id: `${o.id}-install-done`,
          date: kstYmdHyphen(o.installCompletedAt) ?? '',
          category: 'installDone',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          href: `/customers/${o.customerId}`,
        });
      }
      // FB2 §2 — 그릴러 발주(grillOrderedAt). 캘린더의 'order' 카테고리 미러.
      if (o.grillOrderedAt && inRange(o.grillOrderedAt, range.start, range.end)) {
        out.push({
          id: `${o.id}-order`,
          date: kstYmdHyphen(o.grillOrderedAt) ?? '',
          category: 'order',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          time: o.grillOrderedTime,
          href: `/infrastructure`,
        });
      }
      if (o.grillDeliveredAt && inRange(o.grillDeliveredAt, range.start, range.end)) {
        out.push({
          id: `${o.id}-delivery`,
          date: kstYmdHyphen(o.grillDeliveredAt) ?? '',
          category: 'delivery',
          title: o.customer?.displayName ?? '고객사',
          subtitle: o.contract?.humanId,
          time: o.grillDeliveredTime,
          href: `/infrastructure`,
        });
      }
    }
    // FB2 §2 — 유지보수(ServiceOrder) 일정. scheduledFor 가 있고 윈도우 안인
    // 예방정비 건만. 캘린더의 'maintenance' 카테고리 미러.
    // 2026-10-22 — 여러 대 유지보수는 오더 여러 개가 한 일정으로 묶인다(groupId = 첫 오더 id).
    const groupSize = new Map<string, number>();
    for (const so of maintenanceQ.data?.items ?? [])
      if (so.groupId) groupSize.set(so.groupId, (groupSize.get(so.groupId) ?? 0) + 1);
    for (const so of maintenanceQ.data?.items ?? []) {
      if (so.groupId && so.groupId !== so.id) continue;
      const many = so.groupId ? (groupSize.get(so.groupId) ?? 1) : 1;
      if (!so.scheduledFor || !inRange(so.scheduledFor, range.start, range.end)) continue;
      out.push({
        id: `${so.id}-maintenance`,
        date: kstYmdHyphen(so.scheduledFor) ?? '',
        category: 'maintenance',
        // 2026-10-19 오너 — 오더 번호(SO-…) 대신 고객사 이름을 제목으로.
        title: `${
          so.customerDisplayName ??
          so.description?.replace(/^유지보수 — /, '').split('\n')[0] ??
          so.humanId
        }${many > 1 ? ` (${many}대)` : ''}`,
        subtitle: many > 1 ? `${so.humanId} 외 ${many - 1}건` : so.humanId,
        time: so.scheduledTime,
        // 2026-10-19 오너 — 누르면 그 일정이 올린 기록으로. 2026-11-02 부터는 고객사 리포트(그 번호).
        href: maintenanceHref(so),
      });
    }
    for (const c of returnsQ.data?.items ?? []) {
      if (!c.returnScheduledAt || !inRange(c.returnScheduledAt, range.start, range.end)) continue;
      out.push({
        id: `${c.customerId}-return`,
        date: kstYmdHyphen(c.returnScheduledAt) ?? '',
        category: 'return',
        title: c.displayName,
        href: '/field/active-customers',
        time: c.returnScheduledTime,
      });
    }
    for (const r of replacementsQ.data?.items ?? []) {
      if (r.done || !inRange(r.scheduledFor, range.start, range.end)) continue;
      out.push({
        id: `${r.id}-replace`,
        date: kstYmdHyphen(r.scheduledFor) ?? '',
        category: 'replace',
        title: replacementTitle(r),
        subtitle: replacementSubtitle(r),
        time: r.scheduledTime,
        replacement: r,
      });
    }
    for (const r of renewalsQ.data?.items ?? []) {
      if (r.done || !inRange(r.scheduledFor, range.start, range.end)) continue;
      out.push({
        id: `${r.id}-renewal`,
        date: kstYmdHyphen(r.scheduledFor) ?? '',
        category: 'renewal',
        title: renewalTitle(r),
        subtitle: renewalSubtitle(r),
        time: r.scheduledTime,
        renewal: r,
      });
    }
    for (const e of eventsQ.data?.items ?? []) {
      if (e.returned) continue;
      for (const side of ['install', 'return'] as const) {
        const at = side === 'install' ? e.installDate : e.returnDate;
        if (!inRange(at, range.start, range.end)) continue;
        out.push({
          id: `${e.id}-event-${side}`,
          date: kstYmdHyphen(at) ?? '',
          category: 'event',
          title: eventChipTitle(e, side),
          subtitle: eventSubtitle(e),
          time: side === 'install' ? e.installTime : e.returnTime,
          calendarEvent: e,
        });
      }
    }
    // FB2 §2 — 켜진 카테고리만 표시. 정렬: 날짜 오름차순 → 카테고리 표시순서
    // (설치→발주→납품→유지보수)로 다중 이벤트 날짜 안정 정렬.
    const order: Record<CalendarCategory, number> = {
      install: 0,
      installDone: 1,
      order: 2,
      delivery: 3,
      maintenance: 4,
      return: 5,
      replace: 6,
      renewal: 7,
      event: 8,
    };
    const filtered = out.filter((e) => activeCategories[e.category]);
    filtered.sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      // 하루 안에서는 하루 종일 일정이 먼저, 그다음 시각 순.
      const t = (a.time ?? '').localeCompare(b.time ?? '');
      if (t !== 0) return t;
      return order[a.category] - order[b.category];
    });
    return filtered;
  }, [
    ordersQ.data,
    maintenanceQ.data,
    returnsQ.data,
    replacementsQ.data,
    renewalsQ.data,
    eventsQ.data,
    range.start,
    range.end,
    activeCategories,
  ]);

  const grouped = useMemo(() => {
    const m = new Map<string, UpcomingEvent[]>();
    for (const e of events) {
      const arr = m.get(e.date);
      if (arr) arr.push(e);
      else m.set(e.date, [e]);
    }
    return [...m.entries()];
  }, [events]);

  const isFetching = ordersQ.isFetching || maintenanceQ.isFetching;
  // 로봇 교체 줄을 누르면 캘린더 우클릭 [교체 완료]와 같은 창(2026-10-20 오너).
  const role = useAuthStore((st) => st.user?.role);
  const canComplete = !!role && canEditAny(role);
  const [completing, setCompleting] = useState<UpcomingEvent | null>(null);

  return (
    <section style={card} aria-label="다가오는 일정">
      <header style={headerRow}>
        <h3 className="kuun-h3" style={{ margin: 0 }}>
          다가오는 일정
        </h3>
        <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          {isFetching ? '불러오는 중…' : `${events.length}건 · ${days}일`}
        </span>
      </header>

      {/* FB2 §2 (결정 H) — 캘린더와 동일한 카테고리 표시 토글 바. 켜진
          카테고리만 아래 목록에 방출·표시한다. (저장키는 패널 전용.) */}
      <div style={toggleBar} role="group" aria-label="일정 카테고리 표시 토글">
        <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 600 }}>표시:</span>
        {CATEGORY_ORDER.map((k) => {
          const on = activeCategories[k];
          return (
            <label key={k} style={toggleChip(k, on)}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleCategory(k)}
                style={{ margin: 0, cursor: 'pointer' }}
              />
              <Icon name={CATEGORY_META[k].iconName} size={14} aria-hidden />{' '}
              {CATEGORY_META[k].label}
            </label>
          );
        })}
      </div>

      {ordersQ.error && (
        <div style={errorBox} role="alert">
          일정을 불러오지 못했습니다.
        </div>
      )}

      {!ordersQ.error && events.length === 0 && !isFetching && (
        <div style={emptyBox}>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            앞으로 {days}일간 예정된 일정이 없습니다.
          </div>
        </div>
      )}

      {grouped.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {grouped.map(([date, dayEvents]) => (
            <DayBlock
              key={date}
              date={date}
              events={dayEvents}
              onOpen={canComplete ? setCompleting : undefined}
            />
          ))}
        </div>
      )}
      {completing?.replacement && (
        <CompleteReplacementModal
          replacement={completing.replacement}
          date={completing.date}
          onClose={() => setCompleting(null)}
        />
      )}
      {completing?.renewal && (
        <CompleteRenewalModal
          renewal={completing.renewal}
          date={completing.date}
          onClose={() => setCompleting(null)}
        />
      )}
      {completing?.calendarEvent && (
        <EventEditModal event={completing.calendarEvent} onClose={() => setCompleting(null)} />
      )}
    </section>
  );
}

function DayBlock({
  date,
  events,
  onOpen,
}: {
  date: string;
  events: UpcomingEvent[];
  onOpen?: (e: UpcomingEvent) => void;
}) {
  const holiday = isKoreanHoliday(date);
  const weekend = isWeekend(date);
  // '오늘' 배지 — 위 창(range)과 같은 기준(KST)이어야 한다.
  const isToday = date === kstTodayYmd();

  const dateColor = holiday
    ? 'var(--fg-danger)'
    : weekend
      ? new Date(`${date}T00:00:00.000Z`).getUTCDay() === 0
        ? 'var(--fg-danger)'
        : 'var(--blue-800)'
      : 'var(--fg-default)';

  return (
    <div>
      <div style={dayHeaderRow}>
        <span style={{ fontSize: 12, fontWeight: 600, color: dateColor }}>
          {formatHumanDate(date)}
          {isToday && <span style={todayBadge}>오늘</span>}
        </span>
        {holiday && (
          <span style={{ fontSize: 10, color: 'var(--fg-danger)' }}>{getHolidayName(date)}</span>
        )}
      </div>
      <ul style={list}>
        {events.map((e) => (
          <EventRow
            key={e.id}
            event={e}
            onOpen={e.replacement || e.renewal || e.calendarEvent ? onOpen : undefined}
          />
        ))}
      </ul>
    </div>
  );
}

function EventRow({
  event,
  onOpen,
}: {
  event: UpcomingEvent;
  onOpen?: (e: UpcomingEvent) => void;
}) {
  const meta = CATEGORY_META[event.category];
  const inner = (
    <div style={row}>
      <span
        style={{
          ...chip,
          background: meta.solid ? meta.solid[0] : meta.bg,
          color: meta.solid ? meta.solid[1] : meta.color,
        }}
      >
        <Icon name={meta.iconName} size={11} aria-hidden /> {meta.label}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {event.time && <b style={{ fontWeight: 700 }}>{event.time} </b>}
          {event.title}
        </div>
        {event.subtitle && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {event.subtitle}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <li>
      {onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(event)}
          style={rowButton}
          data-testid={`upcoming-open-${event.id}`}
          title="누르면 교체를 마칠 수 있습니다"
        >
          {inner}
        </button>
      ) : event.href ? (
        <Link to={event.href} style={rowLink}>
          {inner}
        </Link>
      ) : (
        inner
      )}
    </li>
  );
}

// ============================================================
// Date helpers
// ============================================================
//
// 한국시간 통일 5차(26-08-14) — 지역 `formatYmd`(UTC 성분 읽기)를 지웠다.
// '지금'과 저장된 값은 전부 정본(kstTodayYmd / kstYmdHyphen)으로 읽는다.

/**
 * 창(window) 안인가 — **위 events 가 찍는 키와 같은 기준**으로 판정해야 한다.
 * 다른 기준으로 자르면 창 경계의 하루가 목록에는 없는데 캘린더에는 있는
 * (또는 그 반대의) 조용한 어긋남이 생긴다.
 */
function inRange(iso: string | Date | null | undefined, start: string, end: string): boolean {
  const ymd = kstYmdHyphen(iso);
  if (!ymd) return false;
  return ymd >= start && ymd <= end;
}

const KO_DAY = ['일', '월', '화', '수', '목', '금', '토'];

function formatHumanDate(date: string): string {
  // Display "M월 D일 (요일)" — e.g. "5월 12일 (화)". The full year is
  // implied by the surrounding panel (today + 14 days never spans a
  // year boundary in practice, and even if it did the dashboard's
  // overall context — "다가오는 일정" — communicates the year).
  const d = new Date(`${date}T00:00:00.000Z`);
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const dow = KO_DAY[d.getUTCDay()];
  return `${m}월 ${day}일 (${dow})`;
}

// ============================================================
// Styles
// ============================================================

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--bg-surface)',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  // Match IntegratedCalendar's general height in compact mode so the
  // 3:2 row reads as a balanced pair rather than a tall calendar +
  // short list. minHeight floor handles months with fewer events.
  minHeight: 320,
};

const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

// FB2 §2 (결정 H) — 카테고리 표시 토글 바 (IntegratedCalendar 와 동일 스타일).
const toggleBar: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
};

function toggleChip(c: CalendarCategory, on: boolean): CSSProperties {
  const m = CATEGORY_META[c];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid',
    borderColor: on ? 'transparent' : 'var(--border-default)',
    background: on ? m.bg : 'transparent',
    color: on ? m.color : 'var(--fg-muted)',
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
  };
}

const errorBox: CSSProperties = {
  padding: 12,
  background: 'var(--bg-danger-subtle)',
  border: '1px solid var(--fg-danger)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--fg-danger)',
  fontSize: 13,
};

const emptyBox: CSSProperties = {
  padding: '24px 12px',
  textAlign: 'center',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-sunken)',
};

const dayHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 6,
  paddingBottom: 4,
  borderBottom: '1px solid var(--border-subtle, var(--border-default))',
  gap: 8,
};

const todayBadge: CSSProperties = {
  marginLeft: 8,
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 999,
  background: 'var(--blue-50)',
  color: 'var(--blue-800)',
  fontWeight: 600,
};

const list: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const row: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 8px',
  borderRadius: 'var(--radius-sm)',
};

const rowLink: CSSProperties = {
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
};

const rowButton: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: 0,
  border: 'none',
  background: 'none',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
};

const chip: CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  fontWeight: 500,
  flexShrink: 0,
  whiteSpace: 'nowrap',
};
